import type { ShapePoint, ShapeVertex } from '@repo/api-contracts';

/** 한 구간의 3차 베지어 제어점. 손잡이가 없는 쪽 제어점은 끝점과 같다(곡선이 아니면 직선과 같다). */
export interface CubicSegment {
  readonly p0: ShapePoint;
  readonly p1: ShapePoint;
  readonly p2: ShapePoint;
  readonly p3: ShapePoint;
  /** 구간 시작 점의 handleOut 이나 끝 점의 handleIn 이 있는가 — flattenPolygonRing 이 곡선으로 푸는 구간. */
  readonly curved: boolean;
}

const plain = (point: ShapePoint): ShapePoint => ({ xMm: point.xMm, yMm: point.yMm });

/** 점 index → 다음 점(마지막은 첫 점)으로 가는 구간. 점이 없으면 null. */
export const segmentOf = (points: readonly ShapeVertex[], index: number): CubicSegment | null => {
  const start = points[index];
  const end = points[(index + 1) % points.length];
  if (start === undefined || end === undefined) return null;
  return {
    p0: plain(start),
    p1: plain(start.handleOut ?? start),
    p2: plain(end.handleIn ?? end),
    p3: plain(end),
    curved: start.handleOut !== undefined || end.handleIn !== undefined,
  };
};

export const lerpPoint = (a: ShapePoint, b: ShapePoint, t: number): ShapePoint => ({
  xMm: a.xMm + (b.xMm - a.xMm) * t,
  yMm: a.yMm + (b.yMm - a.yMm) * t,
});

/** 구간 위 매개변수 t(0..1)의 점. 직선 구간은 선형 보간이다. */
export const segmentPointAt = (segment: CubicSegment, t: number): ShapePoint => {
  if (!segment.curved) return lerpPoint(segment.p0, segment.p3, t);
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return {
    xMm: a * segment.p0.xMm + b * segment.p1.xMm + c * segment.p2.xMm + d * segment.p3.xMm,
    yMm: a * segment.p0.yMm + b * segment.p1.yMm + c * segment.p2.yMm + d * segment.p3.yMm,
  };
};

/**
 * 3차 베지어를 t 에서 둘로 나눈다(de Casteljau). 두 조각을 이어 그리면 원래 곡선과 같다.
 * left 는 [p0, q0, r0, s], right 는 [s, r1, q2, p3] 의 제어점이다.
 */
export const splitCubicAt = (
  segment: CubicSegment,
  t: number,
): {
  readonly q0: ShapePoint;
  readonly r0: ShapePoint;
  readonly s: ShapePoint;
  readonly r1: ShapePoint;
  readonly q2: ShapePoint;
} => {
  const q0 = lerpPoint(segment.p0, segment.p1, t);
  const q1 = lerpPoint(segment.p1, segment.p2, t);
  const q2 = lerpPoint(segment.p2, segment.p3, t);
  const r0 = lerpPoint(q0, q1, t);
  const r1 = lerpPoint(q1, q2, t);
  return { q0, r0, s: lerpPoint(r0, r1, t), r1, q2 };
};
